import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import "dayjs/locale/nl";

import Navbar from "../Navbar/Navbar";
import ScaledCanvas from "../Dashboard/components/ScaledCanvas";
import TopbarClock from "../Dashboard/components/TopbarClock";
import TeamTargetHero from "../Dashboard/components/TeamTargetHero";
import MostSoldServices from "../Dashboard/components/MostSoldServices";
import MonthlyRankList from "../Dashboard/components/MonthlyRankList";
import YearRankList from "../Dashboard/components/YearRankList";
import AnimationLayer from "../Dashboard/animations/AnimationLayer";

import { animations } from "../Dashboard/animations/registry";
import { useAnimations } from "../Dashboard/hooks/useAnimations";
import { useFrameWatch, useRenderTier } from "../Dashboard/hooks/useRenderTier";
import {
  autoRenderTier,
  forceRenderTier,
  isTierForced,
} from "../Dashboard/utils/renderTier";
import { useRankView } from "../Dashboard/hooks/useRankView";
import { useSecretCode } from "../Dashboard/hooks/useSecretCode";
import { diffEvents } from "../Dashboard/hooks/useSaleEvents";
import { targetProgress } from "../Dashboard/utils/targetProgress";
import { bigDealAmount, coinRainAmount } from "../Dashboard/constants";
import { formatCurrency } from "../Dashboard/utils/format";
import { mockData } from "./mockData";
import { labUnlocked, lockLab } from "./labAccess";
import {
  labAmounts,
  labSellers,
  payments,
  applySale,
  paymentSale,
  scenarioFor,
} from "./labData";

import "../Dashboard/Dashboard.css";
import "./AnimationLab.css";

// De kwaliteitstrappen zoals ze in het paneel staan. Zie
// Dashboard/utils/renderTier.js voor wat er per trap aan en uit gaat.
const tierChoices = [
  ["high", "Hoog"],
  ["mid", "Midden"],
  ["low", "Laag"],
];

// Het animatielab: hetzelfde dashboard, maar met verzonnen cijfers en een
// paneel ernaast.
//
// De cijfertoetsen zijn betalingen: 1 t/m 3 boeken €100, €500 en €1.100 bij
// op Jaylano, 4 t/m 6 op Sanne, 7 t/m 9 op Merel. Er verandert dan echt iets
// in de cijfers. De balk groeit, het bedrag telt op, de meter draait door, en
// het dashboard bepaalt daarna zélf welke animaties daarbij horen met precies
// dezelfde vergelijking als live. Klein bedrag is een banner, boven de €1.000
// komt het grove geschut erbij, en wordt daarmee de plek erboven gepakt, dan
// gaat de stijger-animatie vanzelf af.
//
// Daaronder staat de lijst met losse animaties, om er één apart te bekijken.
//
// Het paneel staat buiten de ScaledCanvas, want dat vlak wordt naar het
// scherm geschaald; het paneel moet gewoon leesbaar blijven.

const AnimationLab = () => {
  const { month, year } = useParams();
  const monthName = dayjs(`${year}-${month}`).locale("nl").format("MMMM");
  const previousMonthName = dayjs(`${year}-${month}`)
    .subtract(1, "month")
    .locale("nl")
    .format("MMMM");

  const [data, setData] = useState(mockData);
  const [lastSale, setLastSale] = useState(null);
  // Het paneel begint dicht: je komt hier via de verborgen code binnen en
  // ziet dan eerst het scherm zoals het op de tv staat. Met H haal je het
  // paneel erbij.
  const [panelOpen, setPanelOpen] = useState(false);

  // Terug naar het echte dashboard: typ "nee". Heen ging via "ja" op het
  // dashboard zelf, zie useSecretCode.
  const navigate = useNavigate();
  const back = useCallback(() => {
    lockLab();
    navigate(`/dashboard/${month}/${year}`, { replace: true });
  }, [month, navigate, year]);

  useSecretCode({ nee: back });

  // Het lab mag alleen open als je er via de code binnenkomt. Bij een
  // refresh is die vlag weg (zie labAccess.js) en gaan we meteen terug, dus
  // een achtergebleven /animaties-URL houdt de tv niet in het testscherm.
  const allowed = useRef(labUnlocked()).current;
  useEffect(() => {
    if (!allowed) back();
  }, [allowed, back]);

  const rankView = useRankView(data.rankList.length);
  useFrameWatch();
  const { tier } = useRenderTier();
  const tierFixed = isTierForced();
  // keyboard: false - de toetsen lopen hier via de betalingen hieronder.
  const { active, play, playGroup, stopAll, rootClass } = useAnimations({
    keyboard: false,
  });

  const timers = useRef([]);
  const later = useCallback((fn, ms) => {
    timers.current.push(setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  // De cijfers staan ook in een ref. "Alles achter elkaar" zet een rij
  // timers klaar, en die zouden anders allemaal met de stand van vóór de
  // eerste animatie rekenen; via de ref bouwt elke stap op de vorige door.
  const dataRef = useRef(mockData);
  const update = useCallback((next) => {
    dataRef.current = next;
    setData(next);
  }, []);

  // Er komt een betaling binnen. Wij boeken hem alleen bij; wát er dan
  // gebeurt, bepaalt het dashboard zelf - dezelfde weg als op de tv.
  const receive = useCallback(
    (sale) => {
      const current = dataRef.current;
      const next = applySale(current, sale);

      update(next);
      setLastSale(sale);
      // Via playGroup, net als op de tv: klik je twee keer snel achter
      // elkaar, dan wacht de tweede verkoop netjes op de eerste.
      playGroup(diffEvents(current, next));
    },
    [playGroup, update],
  );

  const pay = useCallback(
    (payment) => receive(paymentSale(dataRef.current, payment)),
    [receive],
  );

  // Eén animatie los bekijken: de cijfers krijgen de verandering die erbij
  // hoort en daarna speelt precies die animatie af.
  const preview = useCallback(
    (id) => {
      const { data: next, payload, sale } = scenarioFor(id, dataRef.current);
      update(next);
      setLastSale(sale);
      play(id, payload);
    },
    [play, update],
  );

  const previewAll = useCallback(() => {
    stopAll();
    let wait = 0;
    animations.forEach((animation) => {
      later(() => preview(animation.id), wait);
      wait += Math.min(animation.ms, 3000) + 400;
    });
  }, [later, preview, stopAll]);

  const reset = useCallback(() => {
    stopAll();
    timers.current.forEach(clearTimeout);
    timers.current = [];
    update(mockData);
    setLastSale(null);
  }, [stopAll, update]);

  useEffect(() => {
    const onKey = (event) => {
      const tag = event.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;

      if (event.key === "Escape") return stopAll();
      if (event.key === " ") {
        event.preventDefault();
        return previewAll();
      }
      if (event.key === "h") return setPanelOpen((open) => !open);
      if (event.key === "r") return reset();
      if (event.key === "0") return preview("target-hit");

      const payment = payments.find((item) => item.key === event.key);
      if (payment) pay(payment);
    };

    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pay, preview, previewAll, reset, stopAll]);

  const progress = targetProgress({
    scored: data.scoredTowardsTarget,
    target: data.teamTarget.amount,
    month,
    year,
  });

  if (!allowed) return null;

  const running = new Set(active.map((run) => run.id));
  const changed = data.amount - mockData.amount;

  return (
    <>
      <ScaledCanvas>
        <div className={`dashboard-container ${rootClass}`}>
          <Navbar />
          <TopbarClock />
          <AnimationLayer active={active} />

          <div className="dash-body">
            <TeamTargetHero
              monthLabel={monthName.toUpperCase()}
              teamTarget={data.teamTarget}
              amount={data.amount}
              progress={progress}
            />

            <section className="dash-cols">
              <MostSoldServices
                soldServices={data.soldServices}
                monthLabel={monthName.toUpperCase()}
              />
              <MonthlyRankList
                rankList={data.rankList}
                rankView={rankView}
                now={monthName}
                bestSeller={data.bestSeller}
                previousMonth={previousMonthName}
                deals={data.deals}
                existantDeals={data.existantDeals}
              />
              <YearRankList yearRankList={data.yearRankList} />
            </section>
          </div>
        </div>
      </ScaledCanvas>

      {/* Zodra je binnen bent mag je wél zien hoe je het paneel openkrijgt:
          dit tabje staat rechtsboven en wijst de H-toets aan. Op het echte
          dashboard staat hier niets - daar is de code het geheim. */}
      {!panelOpen && (
        <button className="lab-tab" onClick={() => setPanelOpen(true)}>
          Animatielab <kbd>H</kbd>
        </button>
      )}

      <aside className={`lab-panel${panelOpen ? "" : " is-hidden"}`}>
        <div className="lab-head">
          <div>
            <h1>Animatielab</h1>
            <p>Testomgeving. De cijfers zijn verzonnen.</p>
          </div>
          <button className="lab-close" onClick={() => setPanelOpen(false)}>
            ✕
          </button>
        </div>

        <div className="lab-block">
          <h2>Betaling binnen</h2>
          <div className="lab-pad">
            <div className="lab-pad-head">
              <span />
              {labAmounts.map((amount) => (
                <span
                  key={amount}
                  className={
                    amount >= bigDealAmount
                      ? "is-big"
                      : amount >= coinRainAmount
                        ? "is-coin"
                        : ""
                  }
                >
                  €{formatCurrency(amount)}
                </span>
              ))}
            </div>

            {labSellers.map((name) => (
              <div key={name} className="lab-pad-row">
                <span className="lab-pad-name">{name}</span>
                {payments.filter((payment) => payment.name === name).map((payment) => (
                  <button
                    key={payment.key}
                    className={`lab-key${
                      payment.amount >= bigDealAmount
                        ? " is-big"
                        : payment.amount >= coinRainAmount
                          ? " is-coin"
                          : ""
                    }`}
                    onClick={() => pay(payment)}
                  >
                    {payment.key}
                  </button>
                ))}
              </div>
            ))}
          </div>

          <div className="lab-status">
            {lastSale ? (
              <>
                <b>
                  {lastSale.name} +€{formatCurrency(lastSale.amount)}
                </b>
                <em>{lastSale.service}</em>
              </>
            ) : (
              <em>Nog niets veranderd. Dit zijn de begincijfers.</em>
            )}
            <span className={`lab-delta${changed ? " is-up" : ""}`}>
              team +€{formatCurrency(Math.max(0, changed))}
            </span>
          </div>

          <p className="lab-note">
            Vanaf €{formatCurrency(coinRainAmount)} komen de munten erbij, vanaf{" "}
            €{formatCurrency(bigDealAmount)} ook de diamantregen. Blijf op{" "}
            {labSellers[2]} drukken: na een paar betalingen gaat{" "}
            {labSellers[2]} voorbij {labSellers[1]}, en dan speelt de
            stijger-animatie vanzelf af zonder dat je hem kiest.
          </p>
        </div>

        <div className="lab-block">
          <h2>Losse animaties</h2>
          <ul className="lab-list">
            {animations.map((animation) => (
              <li key={animation.id}>
                <button
                  className={`lab-item${running.has(animation.id) ? " is-running" : ""}`}
                  onClick={() => preview(animation.id)}
                >
                  <span className="lab-item-text">
                    <b>{animation.name}</b>
                    <em>{animation.hint}</em>
                  </span>
                  {animation.id === "target-hit" && <kbd>0</kbd>}
                  <span className="lab-item-ms">
                    {(animation.ms / 1000).toFixed(1)}s
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div className="lab-block">
          <h2>Zwaarte van het beeld</h2>
          <div className="lab-tiers">
            {tierChoices.map(([id, label]) => (
              <button
                key={id}
                className={`lab-key${tier === id && tierFixed ? " is-on" : ""}`}
                onClick={() => forceRenderTier(id)}
              >
                {label}
              </button>
            ))}
            <button
              className={`lab-key${tierFixed ? "" : " is-on"}`}
              onClick={autoRenderTier}
            >
              Auto
            </button>
          </div>
          <p className="lab-note">
            Staat nu op <b>{tier}</b>
            {tierFixed ? " (vastgezet)" : " (gemeten)"}. Op Auto meet het
            dashboard zelf of dit apparaat de beelden bijhoudt en zakt het een
            trap als dat niet zo is. Zet je hem met de hand, dan blijft die
            keuze staan, ook na herstarten. Op de tv kan dat ook via de URL:
            zet <kbd>?fx=low</kbd> achter het adres, of <kbd>?fx=auto</kbd> om
            het weer los te laten.
          </p>
        </div>

        <div className="lab-block">
          <h2>Erin en eruit</h2>
          <dl className="lab-keys">
            <dt>
              <kbd>ja</kbd>
            </dt>
            <dd>Typ dit op het dashboard om hier te komen.</dd>
            <dt>
              <kbd>nee</kbd>
            </dt>
            <dd>Typ dit hier om terug te gaan naar het echte dashboard.</dd>
            <dt>
              <kbd>H</kbd>
            </dt>
            <dd>
              Paneel weg of terug. Zonder paneel zie je het scherm zoals het op
              de tv staat.
            </dd>
          </dl>
          <p className="lab-note">
            De pagina verversen werkt ook: na een herlaad gaat het lab dicht en
            staat het dashboard er weer. De tv komt zo nooit in het testscherm
            terecht.
          </p>
        </div>

        <div className="lab-foot">
          <button onClick={previewAll}>
            <kbd>Space</kbd> Alles achter elkaar
          </button>
          <button onClick={stopAll}>
            <kbd>Esc</kbd> Animatie stoppen
          </button>
          <button onClick={reset}>
            <kbd>R</kbd> Cijfers terugzetten
          </button>
        </div>
      </aside>
    </>
  );
};

export default AnimationLab;
